import React, { useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  Box,
  Alert,
  Snackbar,
} from '@mui/material';
import { LightMode, DarkMode, Add } from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();
  const { session, loading, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const pendingLogout = useRef(false);
  const handleLogout = async () => {
    if (pendingLogout.current) return;
    pendingLogout.current = true;
    setLoggingOut(true);
    setLogoutError(false);
    try {
      await logout();
      navigate('/', { replace: true });
    } catch {
      setLogoutError(true);
    } finally {
      pendingLogout.current = false;
      setLoggingOut(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', transition: 'background-color 0.2s ease' }}>
      <AppBar position="sticky" color="default">
        <Toolbar sx={{ px: { xs: 2, md: 3 }, flexWrap: 'wrap', rowGap: 1, py: 1 }}>
          <Typography
            variant="h6"
            sx={{
              cursor: 'pointer',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => navigate('/')}
          >
            RSVP Manager
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`} onClick={toggleTheme} color="inherit" size="small" sx={{ mr: 1 }}>
            {mode === 'light' ? <DarkMode /> : <LightMode />}
          </IconButton>
          {!loading && session?.authenticated && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
            {session.can_manage && <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => { window.location.href = '/create'; }}
              sx={{ flexShrink: 0 }}
            >
              Create Event
            </Button>}
            <Button variant="outlined" size="small" onClick={handleLogout} disabled={loggingOut} sx={{ flexShrink: 0 }}>
              {loggingOut ? 'Logging out…' : 'Logout'}
            </Button>
          </Box>}
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Outlet />
      </Container>
      <Snackbar open={logoutError} onClose={() => setLogoutError(false)}>
        <Alert severity="error" onClose={() => setLogoutError(false)}>Unable to log out. Please try again.</Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;
